/**
 * Cloudflare Turnstile client helper.
 * Loads the script once and returns a promise that resolves with a token.
 *
 * If REACT_APP_TURNSTILE_SITE_KEY is not set, captcha is skipped (dev mode):
 * all calls resolve with null and the server will also skip verification.
 */

const SITE_KEY = (process.env.REACT_APP_TURNSTILE_SITE_KEY || '').trim();

let scriptLoaded = null;

function loadScript() {
  if (scriptLoaded) return scriptLoaded;
  scriptLoaded = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return resolve(false);
    if (window.turnstile) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('turnstile script failed to load'));
    document.head.appendChild(script);
  });
  return scriptLoaded;
}

/**
 * Execute an invisible Turnstile challenge and return the token.
 * Returns null if captcha is not configured (dev mode).
 */
export async function getCaptchaToken() {
  if (!SITE_KEY) return null;
  try {
    await loadScript();
    if (!window.turnstile) return null;

    return new Promise((resolve) => {
      // Create hidden container
      let container = document.getElementById('turnstile-hidden-container');
      if (!container) {
        container = document.createElement('div');
        container.id = 'turnstile-hidden-container';
        container.style.position = 'fixed';
        container.style.bottom = '-9999px';
        container.style.left = '-9999px';
        document.body.appendChild(container);
      }
      container.innerHTML = '';

      const widgetId = window.turnstile.render(container, {
        sitekey: SITE_KEY,
        size: 'invisible',
        callback: (token) => {
          resolve(token);
          try { window.turnstile.remove(widgetId); } catch (e) { /* ignore */ }
        },
        'error-callback': () => {
          resolve(null);
          try { window.turnstile.remove(widgetId); } catch (e) { /* ignore */ }
        },
        'expired-callback': () => resolve(null)
      });

      // Safety timeout — if Turnstile never calls back, resolve null after 10s
      setTimeout(() => resolve(null), 10000);
    });
  } catch (err) {
    console.warn('Turnstile error:', err.message);
    return null;
  }
}

export const isCaptchaConfigured = () => !!SITE_KEY;
